import { useState } from 'react';

import { AppIcon, ContentNugget, ICON_SIZES, mapNuggetToCardData } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { getCareerPathCategoryName } from '@backrs_ui/grow-ui/src/pages/CareerPath/util';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { ButtonBase, Box, SxProps, Theme, Typography } from '@mui/material';

const TASK_CARD_STRINGS = appStrings.modules.priorities.taskCard;

type TaskCardNuggetContentProps = Readonly<{
  id: string;
  nugget: ContentNugget;
  onClick: () => void;
}>;

export const TaskCardNuggetContent = ({ id, nugget, onClick }: TaskCardNuggetContentProps) => {
  const [didImageLoadFail, setDidImageLoadFail] = useState(false);

  const careerCategories = useAppSelector((state) => state.careerPath.careerCategories);
  const { imageUrl, isVideo } = mapNuggetToCardData({ nugget });
  const showImage = !!imageUrl && !isVideo && !didImageLoadFail;

  const careerPathCategoryName =
    nugget.variant === 'career-path' && careerCategories.length > 0 && nugget.career_path_info
      ? getCareerPathCategoryName(careerCategories, nugget.career_path_info)
      : undefined;

  return (
    <ButtonBase
      id={id}
      sx={nuggetContainerSx}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={imageUrl}
          alt={nugget.title}
          sx={nuggetImageSx}
          onError={() => setDidImageLoadFail(true)}
        />
      ) : (
        <Box sx={placeholderSx} aria-hidden>
          <AppIcon iconName="Share06" diameter="24px" color="Colors/Text/text-brand-tertiary" />
        </Box>
      )}

      <Box sx={nuggetTextContainerSx}>
        <Typography variant="text-md-medium" sx={nuggetTitleSx}>
          {nugget.title}
        </Typography>

        {careerPathCategoryName && (
          <Typography variant="text-sm-medium">{careerPathCategoryName}</Typography>
        )}

        <Box className="task-card-view-resource" sx={viewResourceSx}>
          <Typography variant="text-sm-regular">{TASK_CARD_STRINGS.viewResource}</Typography>
          <AppIcon iconName="ArrowRight" diameter="16px" color="Colors/Text/text-tertiary" />
        </Box>
      </Box>
    </ButtonBase>
  );
};

const nuggetContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
  padding: backrs.spacing['spacing-3xl'],
  textAlign: 'left',
  '&:hover .task-card-view-resource': {
    textDecoration: 'underline',
  },
});

const nuggetImageSx: SxProps<Theme> = ({ backrs }) => ({
  width: ICON_SIZES['64px'],
  height: ICON_SIZES['64px'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  borderRadius: backrs.radius['radius-md'],
  objectFit: 'cover',
  flexShrink: 0,
});

const placeholderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: ICON_SIZES['64px'],
  height: ICON_SIZES['64px'],
  flexShrink: 0,
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Brand/300']}`,
});

const nuggetTextContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
};

const nuggetTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
});

const viewResourceSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});
